import type { LiteralUnion } from "type-fest";

import { getLogger } from "@expert/logging";

export interface MetricsToServerProps {
    accessToken: string;
    collectorUrl: string;
    metricsNamespace?: string;
}

export type CommonMetricNames =
    | "Start"
    | "Success"
    | "Failure"
    | "Duration"
    | "Event"
    | "Error"
    | "Retry"
    | "Count"
    | "Exhausted";
export type MetricName = LiteralUnion<CommonMetricNames, string>;

export interface ExWoMetricData {
    metrics: {
        /**
         * Name should be as generic as possible, e.g. 'responseTime', 'failure', 'success'
         * Preferably to use predefined names from 'CommonMetricNames` type.
         */
        name: MetricName;
        value: number;
        unit?: string;
    }[];
    /**
     * Dimensions for the metrics, e.g. { 'service': 'CallControls' } or { action: 'getUserStatus' }
     */
    dimensions?: Record<string, string>;
    /**
     * Timestamp for the metrics, defaults to now
     */
    timestamp?: Date | number;
    /**
     * Namespace for the metrics, defaults to 'ExpertWorkspace'
     */
    namespace?: string;
}

const logger = getLogger({
    module: "sendMetrics",
});

let metricsToServerProps: MetricsToServerProps | undefined;

export function initMetrics(props: MetricsToServerProps) {
    metricsToServerProps = props;
}

export async function sendMetrics(metricsData: ExWoMetricData[], sendProps?: MetricsToServerProps) {
    const props = { ...metricsToServerProps, ...sendProps };
    if (!props.accessToken || !props.collectorUrl) {
        logger.warn({ metricsData }, "Cannot send metrics to the backend");
        return;
    }
    const metricsDatWithDefaults: ExWoMetricData[] = metricsData.map((metric) => ({
        namespace: props.metricsNamespace,
        ...metric,
    }));

    const request: RequestInit = {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${props.accessToken}`,
        },
        body: JSON.stringify(metricsDatWithDefaults),
    };

    try {
        await fetch(props.collectorUrl, {
            ...request,
            keepalive: true,
        });
    } catch {
        // Fallback to non-keepalive request
        await fetch(props.collectorUrl, request);
    }
}
